import type { CSSProperties } from "react";
import type { WebOrdersReport } from "../../../api/types";
import { euro } from "../../finances/opcostFormat";
import type { ShopColorSlot } from "../../../utils/shopColorSlots";
import { minutesText, percentOf } from "./webOrdersText";

function notTakenText(counts: { waiting: number; notMade: number; skipped: number }): string {
  const parts = [
    counts.skipped ? `${counts.skipped} skipped` : "",
    counts.waiting ? `${counts.waiting} waiting for a shop` : "",
    counts.notMade ? `${counts.notMade} could not be made` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
}

/**
 * G-143 — one row per shop: what it took, where those orders stand, how
 * fast it accepted, and the money its hand-overs booked. With every shop
 * selected (owners) it also lists the shops that took nothing and the
 * orders no shop took. A shop's name opens that shop's own view.
 */
export function ByShopTable({
  report,
  slots,
  idleShops,
  allShops,
  onPickShop,
}: {
  report: WebOrdersReport;
  slots: Map<string, ShopColorSlot>;
  /** Active shops with no web order in the period (all-shops view only). */
  idleShops: { id: string; name: string }[];
  allShops: boolean;
  onPickShop: ((shopId: string) => void) | null;
}) {
  const total = report.totals.received;
  const topBooked = Math.max(0, ...report.shops.map((s) => Number(s.money.bookedEur)));
  const { notTaken } = report;

  return (
    <div className="table-scroll">
      <table className="wo-table">
        <thead>
          <tr>
            <th>Shop</th>
            <th className="wo-num">Taken</th>
            <th className="wo-num">Through the CRM</th>
            <th className="wo-num">Still open</th>
            <th className="wo-num">Cancelled</th>
            <th className="wo-num">Time to accept</th>
            <th className="wo-num">Customer paid</th>
            {allShops ? <th className="wo-num">Share</th> : null}
            <th>Booked as sales</th>
          </tr>
        </thead>
        <tbody>
          {report.shops.map((s) => (
            <tr key={s.shop.id}>
              <td className="wo-cell-title">
                <span className="wo-swatch" data-slot={slots.get(s.shop.id) ?? "other"} aria-hidden="true" />
                {onPickShop ? (
                  <button type="button" className="wo-link" onClick={() => onPickShop(s.shop.id)}>
                    {s.shop.name}
                  </button>
                ) : (
                  <strong>{s.shop.name}</strong>
                )}
              </td>
              <td className="wo-num" data-label="Taken">{s.received}</td>
              <td className="wo-num" data-label="Through the CRM">{s.handedOver}</td>
              <td className="wo-num" data-label="Still open">{s.stillOpen}</td>
              <td className="wo-num" data-label="Cancelled">{s.cancelled}</td>
              <td className="wo-num" data-label="Time to accept">{minutesText(s.averageMinutesToAccept)}</td>
              <td className="wo-num" data-label="Customer paid">{euro(s.money.customerPaidEur)}</td>
              {allShops ? (
                <td className="wo-num" data-label="Share">{Math.round(percentOf(s.received, total))}%</td>
              ) : null}
              <td data-label="Booked as sales">
                <div className="wo-bar-cell">
                  <span className="wo-num">{euro(s.money.bookedEur)}</span>
                  <div
                    className="wo-bar"
                    style={{ "--share": `${percentOf(Number(s.money.bookedEur), topBooked).toFixed(1)}%` } as CSSProperties}
                  >
                    <span data-slot={slots.get(s.shop.id) ?? "other"} />
                  </div>
                </div>
              </td>
            </tr>
          ))}
          {allShops
            ? idleShops.map((shop) => (
                <tr key={shop.id} className="wo-row-quiet">
                  <td className="wo-cell-title">{shop.name}</td>
                  <td colSpan={8} className="muted">No web orders</td>
                </tr>
              ))
            : null}
          {allShops && notTaken.received > 0 ? (
            <tr className="wo-row-quiet">
              <td className="wo-cell-title">
                <span className="wo-swatch" data-slot="other" aria-hidden="true" />
                Not taken by a shop
              </td>
              <td className="wo-num" data-label="Received">{notTaken.received}</td>
              <td className="wo-num wo-none">—</td>
              <td className="wo-num" data-label="Still open">{notTaken.stillOpen}</td>
              <td className="wo-num" data-label="Cancelled">{notTaken.cancelled}</td>
              <td className="wo-num wo-none">—</td>
              <td className="wo-num wo-none">—</td>
              <td className="wo-num" data-label="Share">{Math.round(percentOf(notTaken.received, total))}%</td>
              {/* No money for orders no shop took: the last cell says where they stand. */}
              <td data-label="Where they stand">{notTakenText(notTaken)}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
